import { CheckCircle2, CircleSlash, ImageOff } from 'lucide-react'
import { useState } from 'react'
import type { CSSProperties } from 'react'

import { ActionInfo, ZoomableImage, formatBytes } from '@krp/ui-kit'

import type { PhotoPreview, PhotoProcessing } from '../model/types'

const sizeText = (width: number | null, height: number | null): string =>
  width === null || height === null ? 'размер не сообщён' : `${width} × ${height} пикселей`

const percent = (value: number): string => `${Math.round(value * 100)} %`

function roiText(roi: readonly number[] | null): string {
  if (roi === null || roi.length !== 4) return 'кадр целиком (рамки нет)'
  return `рамка: слева ${percent(roi[0] ?? 0)}, сверху ${percent(roi[1] ?? 0)}, справа ${percent(roi[2] ?? 1)}, снизу ${percent(roi[3] ?? 1)}`
}

function preprocessingText(processing: PhotoProcessing): string {
  const size = processing.inputSize === null ? '' : ` ${processing.inputSize} × ${processing.inputSize}`
  if (processing.preprocessingKind === 'pad_resize') {
    const pad = processing.padColor === null ? 'без полей' : `поля цвета rgb(${processing.padColor.join(', ')}) до квадрата`
    return `${pad}, затем масштаб до${size || ' квадрата'}`
  }
  if (processing.preprocessingKind === 'hf_processor') return `процессор модели: масштаб и вырезка по центру${size}`
  return 'способ подготовки не сообщён'
}

const dataUrl = (preview: PhotoPreview): string => `data:${preview.contentType};base64,${preview.dataBase64}`

/** Коды причин отсутствия картинки (`PhotoPreviewReasons` сервера, RP-FB58) → слова для администратора. */
const PREVIEW_REASON_TEXT: Readonly<Record<string, string>> = {
  'background-disabled': 'Рамка товара не искалась — опция выключена',
  'media-not-configured': 'Рамка товара не искалась — служба обработки фото недоступна (не подключена)',
  'media-unavailable': 'Рамка товара не искалась — служба обработки фото недоступна',
  'media-rejected': 'Рамка товара не искалась — служба обработки фото не приняла кадр',
  'preview-failed': 'Кадр подготовлен, но превью кадра сделать не удалось',
  'encoder-preview-missing': 'Служба распознавания не прислала превью (нужна версия 0.7.0)',
  'test-scope': 'Рамка товара не искалась — тестовая область ищет по кадру как снят',
}

/**
 * Коды причины «кадр вырезан по рамке товара / рамка не искалась» (`PhotoBackgroundReasons` сервера, RP-FB62) → слова для администратора. Показывается всегда, чтобы было видно не
 * только ЧТО сделано с кадром, но и ПОЧЕМУ.
 */
const BACKGROUND_REASON_TEXT: Readonly<Record<string, string>> = {
  removed: 'Кадр вырезан по рамке товара вместе с фоном — так же, как у фото каталога в индексе',
  disabled: 'Рамка товара не искалась — опция выключена в настройках',
  'test-scope': 'Рамка товара не искалась — тестовая область ищет по кадру как снят',
  'media-not-configured': 'Рамка товара не искалась — служба обработки фото недоступна (не подключена); ответ осторожный, фото сравнивалось с индексом, построенным по рамкам',
  'media-unavailable': 'Рамка товара не искалась — служба обработки фото недоступна; ответ осторожный, фото сравнивалось с индексом, построенным по рамкам',
  'media-rejected': 'Рамка товара не искалась — служба обработки фото не приняла кадр',
}

const VARIANT_TEXT: Readonly<Record<string, string>> = { original: 'исходный кадр', 'product-box': 'кадр по рамке товара с фоном' }

const MODE_TEXT: Readonly<Record<string, string>> = {
  none: 'кадр без обрезки (рамка товара не ищется)',
  'background-removed': 'фон убирается, кадр не обрезается (прежний режим)',
  'background-removed-cropped': 'фон убирается, кадр обрезается по предмету (прежний режим)',
  'background-removed-prepared': 'фон убирается моделью u2net, кадр обрезается по предмету и хранится без потерь (прежний режим)',
  'background-removed-vision': 'фон стирается (Apple Vision), кадр обрезается по предмету (прежний режим)',
  'product-box': 'кадр режется по рамке товара вместе с фоном, запас 10 px и два уменьшения (прежний режим)',
  'product-box-tight': 'кадр режется по плотной рамке товара вместе с фоном и один раз уменьшается до 384×384 по рецепту авторов энкодера, хранится без потерь — один путь для фото каталога и запроса',
}

/** Режим индекса и ход его пересчёта: «пересчитано N из M». Нет данных (прежний сервер) — пусто. */
function indexModeText(processing: PhotoProcessing): string | null {
  if (processing.indexMode === undefined || processing.indexMode === null) return null
  const mode = MODE_TEXT[processing.indexMode] ?? processing.indexMode
  const done = processing.indexRebuildDone
  const total = processing.indexRebuildTotal
  const progress = done === undefined || done === null || total === undefined || total === null ? '' : `; пересчитано ${done} из ${total} товаров с фото`
  return `Режим индекса и запроса: ${mode}${progress}`
}

/** Слова вместо пустого места: код причины известен — его текст; кода нет (прежний сервер) или он новый — честно об этом. */
function missingReasonText(code: string | null | undefined, fallback: string): string {
  if (code === null || code === undefined || code === '') return fallback
  return PREVIEW_REASON_TEXT[code] ?? `Причина: ${code}`
}

/**
 * Плитка раздела. Все плитки одного размера: квадратная ячейка (`aspect-ratio`) и картинка `contain` внутри неё; нет картинки — в той же
 * ячейке плитка-заглушка с причиной словами, а не пустое место.
 */
function Frame({
  title,
  caption,
  src,
  missing,
  reasonCode,
  naturalSize,
}: {
  title: string
  caption: string
  src: string | null
  missing: string
  reasonCode?: string | null | undefined
  /** Картинка показывается в натуральную величину (сторона квадрата, px) на отдельной строке, а не растягивается на плитку. */
  naturalSize?: number | null | undefined
}) {
  const natural = naturalSize !== null && naturalSize !== undefined
  return (
    <figure className="platform-processing__frame" data-natural={natural ? 'yes' : undefined} style={natural ? ({ '--platform-natural-size': `${naturalSize}px` } as CSSProperties) : undefined}>
      <div className="platform-processing__cell" data-state={src === null ? 'missing' : 'image'}>
        {src === null ? (
          <div className="platform-processing__missing" data-reason={reasonCode ?? undefined}>
            <ImageOff size={24} aria-hidden="true" />
            <span>{missing}</span>
          </div>
        ) : (
          <ZoomableImage src={src} alt={title} caption={`${title}: ${caption}`} imageClassName="platform-processing__image" />
        )}
      </div>
      <figcaption>
        <strong>{title}</strong>
        <span>{caption}</span>
      </figcaption>
    </figure>
  )
}

/** Рамка товара из ответа: четыре доли в пределах 0…1, правая и нижняя больше левой и верхней; иначе рисовать нечего. */
function validBox(box: readonly number[] | null | undefined): readonly [number, number, number, number] | null {
  if (box === null || box === undefined || box.length !== 4) return null
  const [left, top, right, bottom] = box
  if (left === undefined || top === undefined || right === undefined || bottom === undefined) return null
  const inside = [left, top, right, bottom].every((value) => value >= 0 && value <= 1)
  return inside && right > left && bottom > top ? [left, top, right, bottom] : null
}

/** Слова под исходным снимком: рамка нарисована / детектор товар не нашёл / рамка не искалась. Не только цвет и контур — ещё и текст. */
function boxCaption(processing: PhotoProcessing, drawn: boolean): string {
  const size = `${sizeText(processing.sourceWidth, processing.sourceHeight)}, ${formatBytes(processing.sourceBytes)}`
  if (drawn) return `${size}; рамкой обведён товар, который нашёл детектор`
  if (processing.productBoxStatus === 'skipped-no-box') return `${size}; рамка не найдена, в модель ушёл кадр целиком`
  return size
}

/**
 * Исходный снимок человека с НАРИСОВАННОЙ рамкой товара (контур + подпись «товар»). Рамка приходит в долях исходного снимка после поворота по EXIF, а браузер показывает снимок
 * тоже с учётом EXIF, поэтому доли ложатся на картинку при любом размере. Сцена повторяет пропорции картинки (по размерам, прочитанным из самой картинки, пока они неизвестны — по
 * размерам из ответа), и рамка не «плывёт» из-за полей вписанной в квадрат картинки.
 */
function OriginalWithBox({ processing, src, box }: { processing: PhotoProcessing; src: string; box: readonly [number, number, number, number] }) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const width = natural?.width ?? processing.sourceWidth ?? 1
  const height = natural?.height ?? processing.sourceHeight ?? 1
  const ratio = width > 0 && height > 0 ? width / height : 1
  const [left, top, right, bottom] = box
  const stage: CSSProperties = { aspectRatio: `${width} / ${height}`, width: ratio >= 1 ? '100%' : `${ratio * 100}%` }
  const frame: CSSProperties = { left: `${left * 100}%`, top: `${top * 100}%`, width: `${(right - left) * 100}%`, height: `${(bottom - top) * 100}%` }
  return (
    <figure className="platform-processing__frame">
      <div className="platform-processing__cell" data-state="image">
        <div className="platform-processing__stage" style={stage}>
          <img
            className="platform-processing__image"
            src={src}
            alt="Исходное"
            onLoad={(event) => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
          />
          {/* Только для человека: модель рамку не видит — ей уходит кадр, вырезанный по ней. Тонкая линия без подписи, чтобы не закрывать детали товара. */}
          <span className="platform-processing__box" style={frame} data-testid="product-box" />
        </div>
      </div>
      <figcaption>
        <strong>Исходное</strong>
        <span>{boxCaption(processing, true)}</span>
      </figcaption>
    </figure>
  )
}

/**
 * «Как обработано фото» (RP-FB55) — раздел внутри «Деталей поиска» (только администратору): рядом ровно три плитки одного размера: исходный снимок, кадр для поиска (рамка товара с фоном) и то, что увидел
 * энкодер; нет картинки — плитка-заглушка с причиной (код от сервера), и ниже размеры, рамка, энкодер и подпись «фон убран / не убирался» с причиной. Картинки живут только
 * в ответе поиска; на сервере не хранятся.
 */
export function ProcessingView({
  processing,
  originalUrl,
  contrast,
}: {
  processing: PhotoProcessing
  originalUrl: string | null
  contrast: number | null
}) {
  const productBox = validBox(processing.productBox)
  return (
    <div className="platform-processing" role="region" aria-label="Как обработано фото">
      <ActionInfo id="platform.search.photo-zoom" label="Увеличить картинку" layout="block">
      <div className="platform-processing__frames">
        {originalUrl !== null && productBox !== null ? (
          <OriginalWithBox processing={processing} src={originalUrl} box={productBox} />
        ) : (
          <Frame
            title="Исходное"
            caption={boxCaption(processing, false)}
            src={originalUrl}
            missing="Исходный снимок в этом окне недоступен"
          />
        )}
        <Frame
          title="Кадр для поиска"
          caption={processing.processedPreview === null ? 'картинки нет' : 'рамка товара вместе с фоном — по нему идёт поиск'}
          src={processing.processedPreview === null ? null : dataUrl(processing.processedPreview)}
          missing={missingReasonText(processing.processedPreviewReason, processing.backgroundRemoved ? 'Кадр подготовлен, но превью не пришло' : 'Рамка товара не искалась')}
          reasonCode={processing.processedPreviewReason}
        />
        <Frame
          title="Что видит энкодер"
          naturalSize={processing.inputSize}
          caption={
            processing.encoderInputPreview === null
              ? 'картинки нет'
              : processing.inputSize === null
                ? 'после подготовки'
                : `так видит модель (${processing.inputSize} × ${processing.inputSize})`
          }
          src={processing.encoderInputPreview === null ? null : dataUrl(processing.encoderInputPreview)}
          missing={missingReasonText(processing.encoderInputPreviewReason, 'Служба распознавания не прислала превью')}
          reasonCode={processing.encoderInputPreviewReason}
        />
      </div>
      </ActionInfo>
      <p className="platform-processing__badge" data-removed={processing.backgroundRemoved ? 'yes' : 'no'}>
        {processing.backgroundRemoved ? <CheckCircle2 size={16} aria-hidden="true" /> : <CircleSlash size={16} aria-hidden="true" />}
        {processing.backgroundRemoved ? 'Кадр вырезан по рамке товара' : 'Рамка товара не искалась'}
        <span> · {processing.backgroundNote}</span>
      </p>
      <ul className="platform-processing__facts" aria-label="Подробности обработки">
        {processing.backgroundReason !== undefined && processing.backgroundReason !== null && (
          <li data-testid="background-reason">Кадр: {BACKGROUND_REASON_TEXT[processing.backgroundReason] ?? `причина ${processing.backgroundReason}`}</li>
        )}
        {indexModeText(processing) !== null && <li data-testid="index-mode">{indexModeText(processing)}</li>}
        {processing.dualQuery === true && (
          <li data-testid="dual-query">
            Индекс смешанный (часть фото пересчитана под рамку товара, часть нет): запрос считался дважды — по исходному кадру и по кадру рамки товара. Лучшее сходство дал вариант «
            {VARIANT_TEXT[processing.bestVariant ?? ''] ?? processing.bestVariant ?? 'не определён'}»
            {processing.originalScore !== undefined && processing.originalScore !== null && processing.cutScore !== undefined && processing.cutScore !== null
              ? ` (исходный ${percent(processing.originalScore)}, по рамке ${percent(processing.cutScore)})`
              : ''}
          </li>
        )}
        <li>Область в энкодер: {roiText(processing.roi)}; {sizeText(processing.cropWidth, processing.cropHeight)}</li>
        <li>Подготовка: {preprocessingText(processing)}</li>
        <li>Энкодер: {processing.encoder ?? 'не сообщён'}</li>
        {contrast !== null && (
          <li>
            Контраст выдачи по фото: {percent(contrast)}
            {contrast < 0.1 ? ' — фото почти не различает кандидатов, им не стоит верить' : ''}
          </li>
        )}
      </ul>
    </div>
  )
}
